import React, { useState, useEffect } from 'react'
import { Shield, ShieldCheck, Key, AlertTriangle } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import MfaSetupWizard from './MfaSetupWizard'
import MfaManageModal from './MfaManageModal'
import MfaTestModal from './MfaTestModal'
import toast from 'react-hot-toast'

const MfaSecurityCard = () => {
  const [mfaStatus, setMfaStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showSetupWizard, setShowSetupWizard] = useState(false)
  const [showManageModal, setShowManageModal] = useState(false)
  const [showTestModal, setShowTestModal] = useState(false)

  const loadMfaStatus = async () => {
    setLoading(true)
    try {
      // Use official Supabase MFA API to list factors
      const { data: factors, error } = await supabase.auth.mfa.listFactors()
      const { data: user } = await supabase.auth.getUser()
      
      if (error) {
        console.error('🔐 Error loading MFA factors:', error)
        setMfaStatus({ totp_enrolled: false })
        return
      }

      const totpFactors = factors?.totp || []
      const isEnrolled = totpFactors.length > 0
      
      console.log('🔐 MFA Status (Official API):', {
        totpFactorsCount: totpFactors.length,
        isEnrolled,
        factors: totpFactors
      })
      
      // Check if user is breakglass (admin bypass) - this would need to be stored in user metadata
      const userEmail = user?.user?.email || ''
      const isBreakglassUser = userEmail.includes('admin@') || userEmail.includes('breakglass@') // Simple check
      
      setMfaStatus({
        totp_enrolled: isEnrolled,
        totp_factors: totpFactors,
        factor_count: totpFactors.length,
        is_breakglass_user: isBreakglassUser,
        user_email: userEmail
      })
      
    } catch (error) {
      console.error('🔐 Failed to load MFA status:', error)
      setMfaStatus({ totp_enrolled: false })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadMfaStatus()
  }, [])

  const handleSetupComplete = () => {
    setShowSetupWizard(false)
    loadMfaStatus()
    toast.success('Two-factor authentication has been enabled successfully!')
  }

  const handleSetupWizardOpen = async () => {
    // Double-check MFA status before opening wizard using official API
    setLoading(true)
    try {
      const { data: factors, error } = await supabase.auth.mfa.listFactors()
      if (!error && factors?.totp && factors.totp.length > 0) {
        toast.error('MFA is already configured for this account!')
        return
      }
    } catch (error) {
      console.error('Error checking MFA status:', error)
    } finally {
      setLoading(false)
    }
    setShowSetupWizard(true)
  }

  const handleMfaDisabled = () => {
    setShowManageModal(false)
    loadMfaStatus()
    toast.success('Two-factor authentication has been disabled')
  }

  const handleMfaDisable = async () => {
    if (!window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      return
    }

    setLoading(true)
    try {
      // Get current factors and unenroll all TOTP factors
      const { data: factors } = await supabase.auth.mfa.listFactors()
      const totpFactors = factors?.totp || []
      
      if (totpFactors.length === 0) {
        toast.error('No MFA factors found to disable')
        return
      }

      // Unenroll all TOTP factors
      for (const factor of totpFactors) {
        const { error } = await supabase.auth.mfa.unenroll({ factorId: factor.id })
        if (error) {
          console.error('Error unenrolling factor:', error)
          toast.error('Failed to disable some MFA factors')
          return
        }
      }
      
      loadMfaStatus()
      toast.success('Two-factor authentication has been disabled')
    } catch (error) {
      console.error('MFA disable error:', error)
      toast.error('Failed to disable MFA. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    )
  }

  const isEnabled = mfaStatus?.totp_enrolled || false
  const enabledDate = mfaStatus?.totp_factors?.[0]?.created_at || mfaStatus?.profile?.mfa_enabled_at
  const isBreakglassUser = mfaStatus?.is_breakglass_user || false

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            {isEnabled ? (
              <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-full">
                <ShieldCheck className="w-5 h-5 text-green-600" />
              </div>
            ) : (
              <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full">
                <Shield className="w-5 h-5 text-gray-400" />
              </div>
            )}
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Two-factor authentication
                {isBreakglassUser && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                    Admin Bypass
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {isEnabled 
                  ? `Your account is protected with two-factor authentication${isBreakglassUser ? ' (admin bypass available)' : ''}`
                  : `Add an extra layer of security to your account${isBreakglassUser ? ' (optional for admin)' : ''}`
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {isEnabled ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5"></div>
                Enabled
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {isBreakglassUser ? 'Optional' : 'Recommended'}
              </span>
            )}
          </div>
        </div>

        {isEnabled && enabledDate && (
          <div className="mt-4 text-xs text-gray-500">
            Enabled on {new Date(enabledDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {isEnabled ? (
              <div className="flex items-center space-x-4">
                <div className="flex items-center text-green-600">
                  <Key className="w-4 h-4 mr-1" />
                  <span>Authenticator app configured</span>
                </div>
              </div>
            ) : (
              <span>Use an authenticator app to generate secure codes</span>
            )}
          </div>

          <div className="flex items-center space-x-3">
            {isEnabled ? (
              <>
                <button
                  onClick={() => setShowTestModal(true)}
                  className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Test MFA
                </button>
                <button
                  onClick={() => setShowManageModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Manage
                </button>
                {!isBreakglassUser && (
                  <button
                    onClick={handleMfaDisable}
                    className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Disable
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={handleSetupWizardOpen}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Set up two-factor authentication
              </button>
            )}
          </div>
        </div>
      </div>

      {showSetupWizard && (
        <MfaSetupWizard
          onClose={() => setShowSetupWizard(false)}
          onComplete={handleSetupComplete}
        />
      )}

      {showManageModal && (
        <MfaManageModal
          mfaStatus={mfaStatus}
          onClose={() => setShowManageModal(false)}
          onMfaDisabled={handleMfaDisabled}
          onRefresh={loadMfaStatus}
        />
      )}

      {showTestModal && (
        <MfaTestModal
          onClose={() => setShowTestModal(false)}
        />
      )}
    </>
  )
}

export default MfaSecurityCard
